/**
 * ticketLineBreakdown.ts — Expande líneas de carrito a filas de ticket con precios.
 *
 * Reglas agnósticas al nicho:
 *  - Si hay variantes o modificadores con precioExtra ≠ 0 → desglose abierto.
 *  - Cabecera del grupo muestra el total acumulado (P.Unit/Cant vacíos).
 *  - Opciones $0 van como texto bajo la cabecera o en línea simple.
 *  - Conteo de uds: solo líneas padre del carrito; hijos ↳ no suman (salvo hub).
 */
import { findSkuByKey } from "@/components/ui/variants/variantHelpers";
import type { CartItem, Producto, VariantGroup } from "@/types";
import type { TicketItem } from "@/features/caja/components/TicketItemsTable";
import { attribucionLinea } from "./discountAttribution";
import {
  formatModifierSummary,
  formatVariantSummary,
  type ModificadorSeleccionado,
  type VarianteSeleccionada,
} from "@/lib/formatLineSelections";

/** Primer sub-línea de un producto con precio base propio. */
export const TICKET_BASE_PRICE_LABEL = "Precio Base";

export const TICKET_SKU_LABEL_PREFIX = "SKU: ";

/** SKU físico de la línea: variante → catálogo por combinación → código del producto. */
export const resolveTicketLineSku = (
  item: CartItem,
  ctx: BreakdownContext,
): string | undefined => {
  const fromCart = item.skuVariante?.trim();
  if (fromCart) return fromCart;

  const catalog = ctx.catalogById.get(item.id);
  if (item.variantKey && catalog?.variantesSKUs?.length) {
    const sku = findSkuByKey(catalog, item.variantKey)?.sku?.trim();
    if (sku) return sku;
  }

  const codigo = item.codigo?.trim() || catalog?.codigo?.trim();
  return codigo || undefined;
};

export const formatTicketSkuLine = (sku: string): string =>
  `${TICKET_SKU_LABEL_PREFIX}${sku.trim()}`;

const attachTicketSku = (
  line: TicketItem,
  item: CartItem,
  ctx: BreakdownContext,
): TicketItem => {
  const sku = resolveTicketLineSku(item, ctx);
  return sku ? { ...line, skuResumen: sku } : line;
};

export type TicketBreakdownOptions = {
  /** Catálogo para resolver precioExtra de variantes y detectar hubs $0. */
  catalog?: Producto[];
};

type PaidModGroup = {
  optionId: string;
  label: string;
  precioExtra: number;
  count: number;
};

type PaidVariantLine = {
  groupLabel: string;
  optionLabel: string;
  precioExtra: number;
};

type SelectionPartition = {
  paidVariants: PaidVariantLine[];
  freeVariants: VarianteSeleccionada[];
  /** Modificadores con ajuste de precio (+ o −). */
  adjustmentMods: ModificadorSeleccionado[];
  freeMods: ModificadorSeleccionado[];
  adjustmentsSum: number;
  /** @deprecated Usar adjustmentMods */
  paidMods: ModificadorSeleccionado[];
  /** @deprecated Usar adjustmentsSum */
  paidExtrasSum: number;
};

type BreakdownContext = {
  catalogById: Map<string, Producto>;
};

const buildCatalogById = (catalog?: Producto[]): Map<string, Producto> =>
  new Map((catalog ?? []).map((p) => [p.id, p]));

/** Catálogo mínimo desde snapshot del carrito/historial cuando falta en inventario local. */
export const synthesizeCatalogFromCartItem = (
  item: CartItem,
): Producto | null => {
  if (!item.id) return null;

  const paidModSum = (item.modificadoresSeleccionados ?? []).reduce(
    (acc, m) => acc + (m.precioExtra ?? 0),
    0,
  );
  const paidVarSum = (item.variantesSeleccionadas ?? []).reduce(
    (acc, v) => acc + (v.precioExtra ?? 0),
    0,
  );
  const unitLine = Math.round(item.precio);
  const catalogPrecio =
    paidModSum !== 0 || paidVarSum > 0
      ? Math.max(0, unitLine - paidModSum - paidVarSum)
      : unitLine;

  return {
    id: item.id,
    nombre: item.nombre,
    precio: catalogPrecio,
    stock: item.stock ?? 0,
    categoria: item.categoria ?? "",
    businessId: item.businessId ?? "",
    costo: item.costo ?? 0,
    stockMinimo: item.stockMinimo ?? 0,
    estado: item.estado ?? "activo",
    createdAt: item.createdAt ?? "",
    updatedAt: item.updatedAt ?? "",
    ...(item.variantes?.length ? { variantes: item.variantes } : {}),
    ...(item.gruposModificadores?.length
      ? { gruposModificadores: item.gruposModificadores }
      : {}),
    ...(item.precioPorKilo != null
      ? { precioPorKilo: item.precioPorKilo }
      : {}),
    ...(item.codigo ? { codigo: item.codigo } : {}),
    ...(item.isForSale != null ? { isForSale: item.isForSale } : {}),
  };
};

const buildBreakdownContext = (
  cart: CartItem[],
  options?: TicketBreakdownOptions,
): BreakdownContext => {
  const catalogById = buildCatalogById(options?.catalog);
  ensureCatalogFromCart(cart, catalogById);
  return { catalogById };
};

const ensureCatalogFromCart = (
  cart: CartItem[],
  catalogById: Map<string, Producto>,
): void => {
  for (const item of cart) {
    if (catalogById.has(item.id)) continue;
    const synthetic = synthesizeCatalogFromCartItem(item);
    if (synthetic) catalogById.set(item.id, synthetic);
  }
};

const getVariantGroups = (
  item: CartItem,
  ctx: BreakdownContext,
): VariantGroup[] | undefined =>
  item.variantes?.length
    ? item.variantes
    : ctx.catalogById.get(item.id)?.variantes;

const getCatalogBasePrice = (
  item: CartItem,
  ctx: BreakdownContext,
): number | null => {
  const catalog = ctx.catalogById.get(item.id);
  if (!catalog) return null;
  return Math.round(catalog.precio);
};

/** precioExtra: selección → catálogo embebido → catálogo externo. */
export const resolveVariantPrecioExtra = (
  item: CartItem,
  sel: VarianteSeleccionada,
  ctx: BreakdownContext,
): number => {
  if (sel.precioExtra != null && sel.precioExtra > 0) return sel.precioExtra;

  const groups = getVariantGroups(item, ctx);
  const group = groups?.find((g) => g.id === sel.groupId);
  const opt = group?.opciones.find((o) => o.id === sel.optionId);
  return opt?.precioExtra ?? 0;
};

const enrichVariantSelections = (
  item: CartItem,
  ctx: BreakdownContext,
): VarianteSeleccionada[] | undefined => {
  const selections = item.variantesSeleccionadas;
  if (!selections?.length) return selections;

  return selections.map((sel) => {
    const extra = resolveVariantPrecioExtra(item, sel, ctx);
    if (extra <= 0) return sel;
    if (sel.precioExtra === extra) return sel;
    return { ...sel, precioExtra: extra };
  });
};

/** Normaliza carrito + catálogo antes de mapear ticket (agnóstico al origen). */
export const enrichCartForTicketBreakdown = (
  cart: CartItem[],
  options?: TicketBreakdownOptions,
): CartItem[] => {
  const ctx = buildBreakdownContext(cart, options);

  return cart.map((item) => {
    const variantes = getVariantGroups(item, ctx);
    const variantesSeleccionadas = enrichVariantSelections(item, ctx);
    return {
      ...item,
      ...(variantes?.length ? { variantes } : {}),
      ...(variantesSeleccionadas?.length ? { variantesSeleccionadas } : {}),
    };
  });
};

const sumModifierAdjustments = (mods: ModificadorSeleccionado[]): number =>
  mods.reduce((acc, m) => acc + (m.precioExtra ?? 0), 0);

const hasModifierAdjustment = (m: ModificadorSeleccionado): boolean =>
  m.precioExtra != null && m.precioExtra !== 0;

export const partitionSelections = (
  item: CartItem,
  ctx: BreakdownContext,
): SelectionPartition => {
  const variants = item.variantesSeleccionadas ?? [];
  const mods = item.modificadoresSeleccionados ?? [];

  const paidVariants: PaidVariantLine[] = [];
  const freeVariants: VarianteSeleccionada[] = [];

  for (const v of variants) {
    const extra = resolveVariantPrecioExtra(item, v, ctx);
    if (extra > 0) {
      paidVariants.push({
        groupLabel: v.groupLabel,
        optionLabel: v.optionLabel,
        precioExtra: extra,
      });
    } else {
      freeVariants.push(v);
    }
  }

  const adjustmentMods = mods.filter(hasModifierAdjustment);
  const freeMods = mods.filter((m) => !hasModifierAdjustment(m));

  const variantAdjustmentsSum = paidVariants.reduce(
    (acc, v) => acc + v.precioExtra,
    0,
  );
  const adjustmentsSum =
    variantAdjustmentsSum + sumModifierAdjustments(adjustmentMods);

  return {
    paidVariants,
    freeVariants,
    adjustmentMods,
    freeMods,
    adjustmentsSum,
    paidMods: adjustmentMods.filter((m) => (m.precioExtra ?? 0) > 0),
    paidExtrasSum: adjustmentsSum,
  };
};

/** ¿Desglosar en sub-líneas? Cualquier ajuste de precio (variante o modificador). */
export const shouldExpandTicketLine = (
  item: CartItem,
  ctx: BreakdownContext,
): boolean => {
  const { paidVariants, adjustmentMods } = partitionSelections(item, ctx);
  return paidVariants.length > 0 || adjustmentMods.length > 0;
};

/** Precio base de catálogo antes de ajustes (+/−). */
export const catalogBaseUnit = (
  item: CartItem,
  ctx: BreakdownContext,
): number => {
  const unitPrice = Math.round(item.precio);
  const { adjustmentsSum } = partitionSelections(item, ctx);
  return Math.max(0, unitPrice - adjustmentsSum);
};

/**
 * Hub de extras: producto con precio catálogo $0 + modificadores pagados.
 * Productos compuestos reales (Desayuno, etc.) tienen base > $0 en catálogo.
 */
export const isExtrasHub = (item: CartItem, ctx: BreakdownContext): boolean => {
  const catalogBase = getCatalogBasePrice(item, ctx);
  if (catalogBase != null && catalogBase > 0) return false;

  const { paidMods } = partitionSelections(item, ctx);
  if (paidMods.length === 0) return false;

  return catalogBaseUnit(item, ctx) <= 0;
};

export const formatPaidVariantLabel = (
  groupLabel: string,
  optionLabel: string,
): string => `+ ${groupLabel}: ${optionLabel}`;

export const formatPaidModifierLabel = (
  optionLabel: string,
  precioExtra?: number,
): string => {
  if (precioExtra != null && precioExtra < 0) return `− ${optionLabel}`;
  return `+ ${optionLabel}`;
};

export const groupPaidModifiers = (
  adjustmentMods: ModificadorSeleccionado[],
): PaidModGroup[] => {
  const map = new Map<string, PaidModGroup>();
  for (const m of adjustmentMods) {
    const prev = map.get(m.optionId);
    if (prev) prev.count += 1;
    else
      map.set(m.optionId, {
        optionId: m.optionId,
        label: m.optionLabel,
        precioExtra: m.precioExtra ?? 0,
        count: 1,
      });
  }
  return [...map.values()];
};

/** Unidad con ajuste de precio (respeta duplicados en selección). */
export const countPaidModifierUnits = (
  mods: ModificadorSeleccionado[],
): number => mods.filter(hasModifierAdjustment).length;

const buildFreeVariantFooterText = (
  freeVariants: VarianteSeleccionada[],
): string | undefined => {
  const text = formatVariantSummary(freeVariants);
  return text || undefined;
};

const buildFreeCustomizationFooterText = (
  freeMods: ModificadorSeleccionado[],
): string | undefined => {
  const text = formatModifierSummary(freeMods);
  return text || undefined;
};

/** Total de cabecera expandida: precio carrito o suma de partes del desglose. */
const computeExpandedHeaderTotal = (
  item: CartItem,
  qty: number,
  baseUnit: number,
  paidVariants: PaidVariantLine[],
  adjustmentMods: ModificadorSeleccionado[],
): number => {
  const fromCart = Math.round(item.precio) * qty;
  if (fromCart > 0) return fromCart;

  let total = baseUnit * qty;
  for (const v of paidVariants) total += v.precioExtra * qty;
  for (const g of groupPaidModifiers(adjustmentMods)) {
    total += g.precioExtra * g.count * qty;
  }
  return total;
};

export const cartItemsToTicketLines = (
  cart: CartItem[],
  options?: TicketBreakdownOptions,
): TicketItem[] => {
  const ctx = buildBreakdownContext(cart, options);
  const enriched = enrichCartForTicketBreakdown(cart, options);
  const lines: TicketItem[] = [];

  for (const item of enriched) {
    const qty = item.quantity || 1;
    const isPeso = !!(item.peso && item.peso > 0);

    if (isPeso) {
      const ppk =
        item.precioPorKilo ??
        (item.peso ? Math.round(item.precio / item.peso) : item.precio);
      lines.push(
        attachTicketSku(
          {
            nombre: item.nombre,
            cantidad: qty,
            precioUnitario: ppk ?? 0,
            peso: item.peso,
            precioPorKilo: ppk,
            lineSubtotal: Math.round(item.precio),
            variantesResumen: formatVariantSummary(item.variantesSeleccionadas),
          },
          item,
          ctx,
        ),
      );
      continue;
    }

    const unitPrice = Math.round(item.precio);
    const { paidVariants, freeVariants, adjustmentMods, freeMods } =
      partitionSelections(item, ctx);
    const baseUnit = catalogBaseUnit(item, ctx);
    const freeVariantFooter = buildFreeVariantFooterText(freeVariants);
    const freeCustomizationFooter = buildFreeCustomizationFooterText(freeMods);

    if (shouldExpandTicketLine(item, ctx)) {
      const headerTotal = computeExpandedHeaderTotal(
        item,
        qty,
        baseUnit,
        paidVariants,
        adjustmentMods,
      );

      lines.push(
        attachTicketSku(
          {
            nombre: item.nombre,
            cantidad: qty,
            precioUnitario: 0,
            lineSubtotal: headerTotal,
            isGroupHeader: true,
          },
          item,
          ctx,
        ),
      );

      if (baseUnit > 0) {
        lines.push({
          nombre: TICKET_BASE_PRICE_LABEL,
          parentNombre: item.nombre,
          isSubLine: true,
          cantidad: qty,
          precioUnitario: baseUnit,
        });
      }

      for (const v of paidVariants) {
        lines.push({
          nombre: formatPaidVariantLabel(v.groupLabel, v.optionLabel),
          parentNombre: item.nombre,
          isSubLine: true,
          cantidad: qty,
          precioUnitario: v.precioExtra,
        });
      }

      for (const g of groupPaidModifiers(adjustmentMods)) {
        lines.push({
          nombre: formatPaidModifierLabel(g.label, g.precioExtra),
          parentNombre: item.nombre,
          isSubLine: true,
          cantidad: g.count * qty,
          precioUnitario: g.precioExtra,
        });
      }

      if (freeVariantFooter) {
        lines.push({
          nombre: "",
          cantidad: 0,
          precioUnitario: 0,
          isGroupFooter: true,
          footerKind: "inventoryVariants",
          opcionesResumen: freeVariantFooter,
          parentNombre: item.nombre,
        });
      }

      if (freeCustomizationFooter) {
        lines.push({
          nombre: "",
          cantidad: 0,
          precioUnitario: 0,
          isGroupFooter: true,
          footerKind: "orderCustomization",
          opcionesResumen: freeCustomizationFooter,
          parentNombre: item.nombre,
        });
      }

      continue;
    }

    const variantText = formatVariantSummary(item.variantesSeleccionadas);
    const modText = formatModifierSummary(
      item.modificadoresSeleccionados ?? [],
    );
    const atrib = attribucionLinea(item);

    const resumenLinea = variantText;

    // Precio de lista original (unitario) cuando la línea cobra menos: el P.UNIT
    // cobrado se tacha con la referencia de lista.
    const listUnit =
      typeof item.precioLista === "number" && item.precioLista > unitPrice
        ? Math.round(item.precioLista)
        : undefined;

    lines.push(
      attachTicketSku(
        {
          nombre: item.nombre,
          cantidad: qty,
          precioUnitario: unitPrice,
          precioUnitarioOriginal: listUnit,
          // Total original de la línea (lista × cantidad) cuando hubo descuentos:
          // se pinta tachado junto al total final en la celda Total del ítem.
          totalOriginalLinea:
            atrib.listaTotal > atrib.cobradoTotal
              ? atrib.listaTotal
              : undefined,
          variantesResumen: resumenLinea || undefined,
          modificadoresResumen: modText || undefined,
          descuentoPct: item.descuento,
        },
        item,
        ctx,
      ),
    );

    // El detalle de la línea explica la transición del precio unitario: el
    // P.UNIT muestra el precio de lista tachado (referencia) y debajo el precio
    // MAYORISTA nuevo (unitario, positivo). El ahorro en $ de cada descuento se
    // consolida en Totales. Cliente VIP se muestra como su % de descuento.
    const pushLineaPrecio = (label: string, neto: number, negativo = false) => {
      if (neto === 0) return;
      const signo = negativo ? -1 : 1;
      lines.push({
        nombre: label,
        cantidad: 0,
        precioUnitario: 0,
        lineSubtotal: signo * Math.abs(Math.round(neto)),
        isSubLine: true,
        parentNombre: item.nombre,
        esDescuentoPrecioLine: true,
      });
    };
    if (item.esMayorista === true && atrib.descuentoMayorista > 0) {
      const mayUnit =
        typeof item.precioMayorista === "number" && item.precioMayorista > 0
          ? Math.round(item.precioMayorista)
          : 0;
      pushLineaPrecio("Precio mayorista", mayUnit);
    }
    if (item.esVIP === true && atrib.descuentoVIP > 0) {
      const pct = Math.max(0, Math.round(item.vipDescuentoPct ?? 0));
      pushLineaPrecio(
        `Cliente VIP${pct > 0 ? ` ${pct}%` : ""}`,
        atrib.descuentoVIP,
        true,
      );
    }
    // Descuento por ítem (manual/catálogo) aplicado a ESTA línea: se especifica
    // aquí con su monto para que el detalle coincida con Totales.
    if (atrib.descuentoApilado > 0) {
      const adj = item.lineAdjustment;
      const pctItem =
        adj?.mode === "discountPercent" && (adj.discountPercent ?? 0) > 0
          ? Math.max(0, Math.round(adj.discountPercent ?? 0))
          : 0;
      const etiquetaItem =
        adj?.mode === "discountPercent"
          ? pctItem > 0
            ? `Descuento por ítem (${pctItem}%)`
            : "Descuento por ítem"
          : "Ajuste de precio";
      pushLineaPrecio(etiquetaItem, atrib.descuentoApilado, true);
    }
  }

  return lines;
};
